import React, {FC, ReactNode, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {Loader, LoaderVariantsEnum} from '../../../../libs/shared/ui/src/components/loaders/loader';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useAppDispatch} from '../../../helpers/appReduxHelpers';
import {SettingsRootPagesEnum} from '../../../models/url/models/types';
import {useHomeShortcutClick} from '../../../settings/home/settingsHomeShortcutHook';
import {useOnboardingModalState} from '../../app/topBar/onboarding/onboardingContext';
import {AdminOnboardingTaskDefinition} from '../admin/tasks/adminOnboardingTypes';

/*
 * Props.
 */

export interface AdminSetupGuideTaskProps {
  number: number;
  title: ReactNode;
  description: ReactNode;
  isComplete: boolean | undefined;
  isExternal: boolean;
  details: ReactNode;
  startTask: AdminOnboardingTaskDefinition['startTask'];
  onInternalTaskStart: (props: AdminSetupGuideTaskProps) => void;
}

/*
 * Style.
 */

const StyledChromelessButton = styled(ChromelessButton)`
  padding: 16px;
  align-items: center;
  border-radius: 12px;
  border: 1px solid ${(p) => p.theme.alphas.black20};
  background: ${(p) => p.theme.greys.white};
  width: 100%;

  &:hover {
    background: ${(p) => p.theme.greys.shade20};
  }
`;

const StyledContentContainerDiv = styled.div`
  display: flex;
  flex: 1 0 0;
  gap: 8px;
  align-items: center;
`;

const StyledContentDiv = styled.div`
  display: flex;
  align-self: flex-start;
`;

const StyledNumberDiv = styled.div`
  display: flex;
  width: 17px;
  height: 17px;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  border: 1.5px solid ${(p) => p.theme.greys.shade70};
  color: ${(p) => p.theme.greys.shade70};
  font-size: 9px;
  font-weight: ${fontWeights.black};
  text-align: center;
`;

const StyledBodyContainerDiv = styled.div`
  flex-grow: 1;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  padding-right: 16px;
`;

const StyledBodyDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledTitleContainerDiv = styled.div`
  display: flex;
`;

const StyledTitleDiv = styled.div`
  color: ${(p) => p.theme.greys.shade90};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.semibold};
  line-height: 114.286%;
`;

const StyledDescriptionDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
`;

const StyledCompletedDiv = styled.div`
  display: flex;
  align-items: center;
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};
  line-height: 150%;
  color: ${(p) => p.theme.palette.green.shade50};
`;

const StyledIcon = styled(Icon)`
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  margin: 0 4px;
`;

/*
 * Component.
 */

export const AdminSetupGuideTask: FC<AdminSetupGuideTaskProps> = (props) => {
  const startTaskContext = useStartTaskContext();
  const [isLoading, setIsLoading] = useState(false);

  const onTaskStart = () => {
    if (!props.isExternal) {
      props.onInternalTaskStart(props);
    }
  };

  const onClick = async () => {
    if (isLoading) {
      return;
    }

    const maybePromise = props.startTask(startTaskContext);
    if (!maybePromise) {
      onTaskStart();
      return;
    }

    setIsLoading(true);
    try {
      await maybePromise;
      onTaskStart();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StyledChromelessButton onClick={onClick}>
      <StyledContentContainerDiv>
        <StyledContentDiv>
          <StyledNumberDiv>{props.number}</StyledNumberDiv>
        </StyledContentDiv>
        <StyledBodyContainerDiv>
          <StyledBodyDiv>
            <StyledTitleContainerDiv>
              <StyledTitleDiv>{props.title}</StyledTitleDiv>
              {props.details && (
                <StyledTooltipCoordinator render={() => props.details} maxWidth={299} placement="top" />
              )}
            </StyledTitleContainerDiv>
            <StyledDescriptionDiv>{props.description}</StyledDescriptionDiv>
          </StyledBodyDiv>
          {props.isComplete && (
            <StyledCompletedDiv>
              <Trans id="admin-setup-guide-task-completed">Completed</Trans>
            </StyledCompletedDiv>
          )}
        </StyledBodyContainerDiv>
        {isLoading ? (
          <Loader variant={LoaderVariantsEnum.GREY} size={VisualSizesEnum.SMALL} />
        ) : (
          <StyledIcon name={props.isExternal ? 'externalLink' : 'chevronRight'} />
        )}
      </StyledContentContainerDiv>
    </StyledChromelessButton>
  );
};

function useStartTaskContext() {
  const dispatch = useAppDispatch();
  const {setIsAdminWizardOpen} = useOnboardingModalState();
  const {onShortcutClick} = useHomeShortcutClick({rootPage: SettingsRootPagesEnum.ADMIN_HOME});

  return {
    dispatch,
    openAdminWizard: () => setIsAdminWizardOpen(true),
    openSettingsHomeShortcut: onShortcutClick,
  };
}
